<script setup>
import { computed, ref } from 'vue';
import { useAdmin } from 'dashboard/composables/useAdmin';
import { useConversationLabels } from 'dashboard/composables/useConversationLabels';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { isHexColor } from 'dashboard/helper/labelColor';
import LabelDropdown from 'shared/components/ui/label/LabelDropdown.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const MAX_VISIBLE_LABELS = 2;

const { isAdmin } = useAdmin();

const {
  savedLabels,
  activeLabels,
  accountLabelsByRecentUse,
  addLabelToConversation,
  removeLabelFromConversation,
} = useConversationLabels();

const visibleLabels = computed(() =>
  activeLabels.value.slice(0, MAX_VISIBLE_LABELS)
);
const hiddenLabels = computed(() =>
  activeLabels.value.slice(MAX_VISIBLE_LABELS)
);
const hiddenLabelsTooltip = computed(() => ({
  content: hiddenLabels.value
    .map(({ title, color }) =>
      isHexColor(color)
        ? `<span class="inline-block w-2 h-2 rounded-sm" style="background: ${color}"></span> ${title}`
        : `${color} ${title}`
    )
    .join('<br>'),
  html: true,
}));
const hiddenLabelsCount = computed(() => `+${hiddenLabels.value.length}`);

const showLabelDropdown = ref(false);
const dropdownLabels = ref([]);

const toggleLabelDropdown = () => {
  showLabelDropdown.value = !showLabelDropdown.value;
  if (showLabelDropdown.value) {
    dropdownLabels.value = accountLabelsByRecentUse.value;
  }
};

const closeLabelDropdown = () => {
  showLabelDropdown.value = false;
};

/**
 * digitt: DENG-7566 — "L" opens the header dropdown only while the contact
 * sidebar is closed; when it is open, the sidebar LabelBox keeps owning the
 * shortcut (both mounted would double-toggle).
 */
const { uiSettings } = useUISettings();

useKeyboardEvents({
  KeyL: {
    action: e => {
      if (uiSettings.value?.is_contact_sidebar_open) return;
      e.preventDefault();
      toggleLabelDropdown();
    },
  },
  Escape: {
    action: () => {
      if (showLabelDropdown.value) {
        closeLabelDropdown();
      }
    },
    allowOnFocusedInput: true,
  },
});
</script>

<template>
  <div
    v-on-clickaway="closeLabelDropdown"
    class="relative flex items-center gap-2 min-w-0"
    @keyup.esc="closeLabelDropdown"
  >
    <div v-if="visibleLabels.length" class="flex items-center min-w-0">
      <div class="flex items-center min-w-0 overflow-hidden">
        <woot-label
          v-for="label in visibleLabels"
          :key="label.id"
          :title="label.title"
          :description="label.description"
          show-close
          :color="label.color"
          variant="smooth"
          class="!mb-0 flex-shrink-0"
          @remove="removeLabelFromConversation"
        />
      </div>
      <span
        v-if="hiddenLabels.length"
        v-tooltip.bottom="hiddenLabelsTooltip"
        class="inline-flex items-center flex-shrink-0 h-6 p-1 text-xs font-medium border border-solid rounded-[4px] cursor-default bg-transparent text-n-slate-11 dark:text-n-slate-12 border-n-strong"
      >
        {{ hiddenLabelsCount }}
      </span>
    </div>
    <NextButton
      v-tooltip.bottom="$t('CONTACT_PANEL.LABELS.CONVERSATION.ADD_BUTTON')"
      icon="i-lucide-tag"
      color="slate"
      :variant="showLabelDropdown ? 'solid' : 'faded'"
      sm
      @click="toggleLabelDropdown"
    />
    <div
      v-if="showLabelDropdown"
      class="absolute top-8 left-0 xl:left-auto xl:right-0 z-[9999] w-72 max-w-[80vw] border rounded-lg bg-n-alpha-3 backdrop-blur-[100px] shadow-lg border-n-strong dark:border-n-strong p-2 box-border"
    >
      <LabelDropdown
        class="!max-h-[30rem]"
        :account-labels="dropdownLabels"
        :selected-labels="savedLabels"
        :allow-creation="isAdmin"
        @add="addLabelToConversation"
        @remove="removeLabelFromConversation"
      />
    </div>
  </div>
</template>
